import { z } from 'zod';

import { CursorPaginationMetaSchema, PaginationSchema } from '../../pagination.js';

import {
  VehicleEnginePreviewSchema,
  VehicleGenerationPreviewSchema,
  VehicleTrimPreviewSchema,
} from './fitment/vehicle-application.schemas.js';

export const CatalogGenericVehicleApplicationParamsSchema = z.object({
  genericProductId: z.uuid(),
});

export const CatalogGenericVehicleApplicationIdParamsSchema = z.object({
  genericProductId: z.uuid(),
  applicationId: z.uuid(),
});

export const CatalogProductVehicleApplicationParamsSchema = z.object({
  catalogProductId: z.uuid(),
});

export const CatalogProductVehicleApplicationIdParamsSchema = z.object({
  catalogProductId: z.uuid(),
  applicationId: z.uuid(),
});

export const CreateCatalogProductVehicleApplicationBodySchema = z.strictObject({
  vehicleModelId: z.uuid(),
  manufactureYearStart: z.number().int().min(1900).max(2100),
  manufactureYearEnd: z.number().int().min(1900).max(2100).optional(),
  vehicleEngineIds: z.array(z.uuid()).default([]),
  vehicleGenerationIds: z.array(z.uuid()).default([]),
  vehicleTrimIds: z.array(z.uuid()).default([]),
});

/** Kept as an alias so existing web imports typecheck until the FE wave. */
export const CreateCatalogGenericVehicleApplicationBodySchema =
  CreateCatalogProductVehicleApplicationBodySchema;

export const CreateCatalogVehicleApplicationResponseSchema = z.object({
  applicationId: z.uuid(),
});

export const CatalogGenericVehicleApplicationListItemSchema = z.object({
  id: z.uuid(),
  genericProductId: z.uuid(),
  vehicleModelId: z.uuid(),
  manufactureYearStart: z.number().int(),
  manufactureYearEnd: z.number().int().nullable(),
  engines: z.array(VehicleEnginePreviewSchema),
  generations: z.array(VehicleGenerationPreviewSchema),
  trims: z.array(VehicleTrimPreviewSchema),
});

export const CatalogProductVehicleApplicationListItemSchema = z.object({
  id: z.uuid(),
  catalogProductId: z.uuid(),
  vehicleModelId: z.uuid(),
  manufactureYearStart: z.number().int(),
  manufactureYearEnd: z.number().int().nullable(),
  engines: z.array(VehicleEnginePreviewSchema),
  generations: z.array(VehicleGenerationPreviewSchema),
  trims: z.array(VehicleTrimPreviewSchema),
});

export const ListCatalogGenericVehicleApplicationsResponseSchema = z.object({
  items: z.array(CatalogGenericVehicleApplicationListItemSchema),
});

export const ListCatalogProductVehicleApplicationsResponseSchema = z.object({
  items: z.array(CatalogProductVehicleApplicationListItemSchema),
});

export const ListApplicableCatalogGenericProductsQuerySchema = PaginationSchema.extend({
  vehicleModelId: z.uuid(),
  manufactureYear: z.coerce
    .number()
    .int()
    .min(1900)
    .max(2100)
    .optional()
    .describe(
      'Required in practice whenever a linked concrete application has a year window (BR-PRD-107). Omitting it returns APPLICABLE_CATALOG_MANUFACTURE_YEAR_REQUIRED instead of an uncertain match (BR-PRD-79/108). Unlike tenant fitment (BR-PRD-23), concrete catalog applications never omit the start year.',
    ),
  vehicleEngineId: z.uuid().optional(),
  vehicleGenerationId: z.uuid().optional(),
  vehicleTrimId: z.uuid().optional(),
});

export const ApplicableCatalogGenericProductItemSchema = z.object({
  id: z.uuid(),
  canonicalName: z.string(),
  categoryId: z.uuid(),
  categoryName: z.string(),
});

export const ListApplicableCatalogGenericProductsResponseSchema = z.object({
  items: z.array(ApplicableCatalogGenericProductItemSchema),
  pagination: CursorPaginationMetaSchema,
});

export type CatalogGenericVehicleApplicationParams = z.infer<
  typeof CatalogGenericVehicleApplicationParamsSchema
>;
export type CatalogGenericVehicleApplicationIdParams = z.infer<
  typeof CatalogGenericVehicleApplicationIdParamsSchema
>;
export type CatalogProductVehicleApplicationParams = z.infer<
  typeof CatalogProductVehicleApplicationParamsSchema
>;
export type CatalogProductVehicleApplicationIdParams = z.infer<
  typeof CatalogProductVehicleApplicationIdParamsSchema
>;
export type CreateCatalogGenericVehicleApplicationBody = z.infer<
  typeof CreateCatalogGenericVehicleApplicationBodySchema
>;
export type CreateCatalogProductVehicleApplicationBody = z.infer<
  typeof CreateCatalogProductVehicleApplicationBodySchema
>;
export type CreateCatalogVehicleApplicationResponse = z.infer<
  typeof CreateCatalogVehicleApplicationResponseSchema
>;
export type CatalogGenericVehicleApplicationListItem = z.infer<
  typeof CatalogGenericVehicleApplicationListItemSchema
>;
export type CatalogProductVehicleApplicationListItem = z.infer<
  typeof CatalogProductVehicleApplicationListItemSchema
>;
export type ListCatalogGenericVehicleApplicationsResponse = z.infer<
  typeof ListCatalogGenericVehicleApplicationsResponseSchema
>;
export type ListCatalogProductVehicleApplicationsResponse = z.infer<
  typeof ListCatalogProductVehicleApplicationsResponseSchema
>;
export type ListApplicableCatalogGenericProductsQuery = z.infer<
  typeof ListApplicableCatalogGenericProductsQuerySchema
>;
export type ApplicableCatalogGenericProductItem = z.infer<
  typeof ApplicableCatalogGenericProductItemSchema
>;
export type ListApplicableCatalogGenericProductsResponse = z.infer<
  typeof ListApplicableCatalogGenericProductsResponseSchema
>;
